import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/base/Breadcrumbs";
import { newsList } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

const ALL = "全部";

export default function NewsCategoryPage() {
  usePageMeta(
    "奇优影院-影视行业最新资讯",
    "奇优影院影视资讯专题，聚合上映预告、行业动态、幕后花絮与深度专题报道，第一时间了解电影与剧集的最新动向。",
    "影视资讯,上映预告,行业动态,幕后花絮,奇优影院",
  );

  const [category, setCategory] = useState(ALL);
  const categories = useMemo(
    () => [ALL, ...Array.from(new Set(newsList.map((item) => item.category)))],
    [],
  );
  const articles = category === ALL ? newsList : newsList.filter((item) => item.category === category);
  const related = newsList.filter((item) => item.category !== category).slice(0, 4);

  return (
    <SiteLayout>
      <div className="max-w-7xl mx-auto px-4 md:px-6 pt-5 md:pt-7">
        <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "影视资讯" }]} />

        <div className="rounded-lg bg-primary-950 px-4 py-6 md:px-8 md:py-9 mb-5">
          <p className="text-[11px] tracking-[0.22em] text-accent-400 font-label uppercase mb-2">Newsroom</p>
          <h1 className="text-2xl md:text-3xl font-extrabold text-background-50 font-heading">
            影视资讯 · 上映预告与行业动态
          </h1>
          <p className="text-sm text-background-300/90 mt-2.5 max-w-2xl leading-relaxed">
            从定档消息到幕后制作，从行业趋势到剧集复盘，编辑部每天整理值得一看的影视动态，帮你在开播与上映前做出判断。
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_300px] gap-5 md:gap-6 items-start">
          <div>
            <div className="flex flex-wrap items-center gap-1.5 mb-4">
              {categories.map((item) => (
                <button
                  key={item}
                  type="button"
                  onClick={() => setCategory(item)}
                  aria-pressed={category === item}
                  className={`px-3 h-8 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap border ${
                    category === item
                      ? "bg-primary-700 border-primary-700 text-background-50 font-medium"
                      : "bg-background-50 border-background-200 text-foreground-600 hover:border-accent-400 hover:text-accent-700"
                  }`}
                >
                  {item}
                </button>
              ))}
              <span className="ml-auto text-xs text-foreground-500">
                共 {articles.length} 篇
              </span>
            </div>

            <div className="space-y-3">
              {articles.map((item) => (
                <article key={item.id}>
                  <Link
                    to={`/news/${item.id}`}
                    className="group grid grid-cols-1 sm:grid-cols-[200px_minmax(0,1fr)] gap-3 sm:gap-4 p-3 rounded-lg border border-background-200 bg-background-50 hover:border-primary-300 transition-colors cursor-pointer"
                  >
                    <div className="w-full h-40 sm:h-[132px] rounded-md overflow-hidden bg-primary-900">
                      <img
                        src={item.image}
                        alt={`${item.title}资讯配图`}
                        title={`${item.title} 影视资讯`}
                        className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.03]"
                      />
                    </div>
                    <div className="min-w-0 flex flex-col">
                      <div className="flex items-center gap-2 text-[11px] font-label">
                        <span className="px-1.5 py-0.5 rounded bg-accent-100 text-accent-800">{item.category}</span>
                        <span className="text-foreground-400">{item.date}</span>
                      </div>
                      <h2 className="text-base md:text-lg font-semibold text-foreground-950 mt-1.5 leading-snug group-hover:text-accent-700 transition-colors">
                        {item.title}
                      </h2>
                      <p className="text-sm text-foreground-600 mt-1.5 leading-relaxed line-clamp-2">
                        {item.summary}
                      </p>
                      <span className="mt-auto pt-2 inline-flex items-center gap-1 text-xs text-primary-600 group-hover:text-accent-600 transition-colors">
                        阅读全文
                        <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
                      </span>
                    </div>
                  </Link>
                </article>
              ))}
            </div>
          </div>

          <aside className="space-y-4">
            <div className="rounded-lg border border-background-200 bg-background-50 p-4">
              <h2 className="text-sm font-bold text-foreground-950 font-heading mb-3">相关文章推荐</h2>
              <ul className="space-y-3">
                {related.map((item) => (
                  <li key={item.id}>
                    <Link to={`/news/${item.id}`} className="group flex gap-2.5 cursor-pointer">
                      <span className="w-1.5 h-1.5 rounded-full bg-accent-500 mt-2 shrink-0" />
                      <span className="min-w-0">
                        <span className="block text-sm text-foreground-800 leading-snug group-hover:text-accent-700 transition-colors line-clamp-2">
                          {item.title}
                        </span>
                        <span className="block text-[11px] text-foreground-400 mt-0.5">{item.date}</span>
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-lg border border-background-200 bg-secondary-50 p-4">
              <p className="text-xs tracking-[0.14em] text-secondary-900 font-label uppercase mb-1.5">编辑说明</p>
              <p className="text-xs text-foreground-600 leading-relaxed">
                本页资讯均为虚构示例内容，用于展示影视资讯站的信息组织方式，不代表任何真实影片或机构动态。
              </p>
              <Link
                to="/about"
                className="mt-3 inline-flex items-center gap-1 text-xs text-primary-700 hover:text-accent-700 transition-colors cursor-pointer"
              >
                查看平台说明
                <i className="ri-arrow-right-line w-3.5 h-3.5 flex items-center justify-center" />
              </Link>
            </div>
          </aside>
        </div>
      </div>
    </SiteLayout>
  );
}